import { Injectable, signal, computed, Signal } from '@angular/core';

/**
 * Publishes the browser's online/offline status as an Angular signal so
 * any component can react to connectivity changes without rxjs plumbing.
 *
 * `navigator.onLine` is the primary source of truth. It's not perfect —
 * some OSes report "online" when connected to a captive Wi-Fi portal with
 * no real Internet — but it's the standard primitive every browser has,
 * and for a fishing PWA "the cell signal just dropped in the bay" it
 * flips to false reliably when it needs to.
 *
 * The service starts listening in its constructor, which runs as soon as
 * it's first injected. Anywhere that needs the value can read
 * `networkStatus.isOnline()` in a template or effect.
 */
@Injectable({ providedIn: 'root' })
export class NetworkStatusService {
  private readonly _isOnline = signal<boolean>(this.readCurrent());

  readonly isOnline: Signal<boolean> = this._isOnline.asReadonly();

  /**
   * Convenience derived signal. Sometimes it reads better to bind to
   * the negative form (e.g. `*ngIf="networkStatus.isOffline()"` for an
   * offline banner).
   */
  readonly isOffline = computed(() => !this._isOnline());

  constructor() {
    // addEventListener rather than onXxxx so a second handler on the
    // window (e.g. tests, analytics) doesn't clobber ours.
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  private readCurrent(): boolean {
    // `navigator.onLine` can be undefined in non-browser test envs;
    // default to "online" so code paths that gate on connectivity
    // don't spuriously refuse to run.
    if (typeof navigator === 'undefined') return true;
    return navigator.onLine;
  }

  private readonly handleOnline = (): void => {
    this._isOnline.set(true);
  };

  private readonly handleOffline = (): void => {
    this._isOnline.set(false);
  };
}
